import React, { useEffect, useState } from 'react';
import {
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  RotateCcw,
  MapPin,
  Clock,
  User as UserIcon,
  FileText,
  Volume2,
  RefreshCw,
  Download,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { sessionService } from '../services/SessionService';
import { parentSummaryService } from '../services/ParentSummaryService';
import { generateAndStoreSessionPdf } from '../services/SessionPdfService';
import { Session, SESSION_TYPE_LABELS } from '../models/Session';
import { fieldsFor, getTemplate } from '../models/noteTemplates';

/* ------------------------------ helpers -------------------------------- */

const fmtDateTime = (d: Date) =>
  d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
  ' · ' +
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const timeSince = (d: Date | null, now: number) => {
  if (!d) return '';
  const mins = Math.max(0, Math.round((now - d.getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ${mins % 60}m ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const durationLabel = (s: Session) => {
  const start = s.actualStart || s.scheduledStart;
  const end = s.actualEnd || s.scheduledEnd;
  const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

/* ------------------------------ styles --------------------------------- */

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  padding: 'var(--spacing-5)',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-5)',
  backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-semibold)',
  cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'transparent',
  color: 'var(--color-primary)',
  border: '1px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-medium)',
  cursor: 'pointer',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 'var(--text-xl)',
  fontWeight: 'var(--font-bold)',
  color: 'var(--color-text-primary)',
};

const meta: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
};

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--spacing-4)',
  zIndex: 200,
};

const modalCard: React.CSSProperties = {
  ...card,
  width: '100%',
  maxWidth: 520,
  padding: 'var(--spacing-6)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontFamily: 'var(--font-family-base)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-white)',
};

/* ------------------------------ page ----------------------------------- */

export const BcbaReviewNotes: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [now, setNow] = useState<number>(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [reviewedOpen, setReviewedOpen] = useState(false);

  const [selected, setSelected] = useState<Session | null>(null);
  const [dictationOpen, setDictationOpen] = useState(false);

  // Modals + action state
  const [showApprove, setShowApprove] = useState(false);
  const [showRevision, setShowRevision] = useState(false);
  const [signature, setSignature] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Post-approval parent summary confirmation
  const [approvedFlow, setApprovedFlow] = useState<{
    session: Session;
    summary: string;
    loading: boolean;
    error: boolean;
    edited: boolean;
  } | null>(null);

  const authorized = !user || user.role === 'supervisor' || user.role === 'admin';

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user || !authorized) return;
    const unsub = sessionService.subscribeCompanySessions(
      user.companyId,
      (live) => setSessions(live),
      () => setError('Live updates unavailable (check Firestore access).')
    );
    return () => unsub();
  }, [user, authorized]);

  if (!authorized) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          You don't have access to Review Notes. This screen is for supervisors and admins.
        </div>
      </div>
    );
  }

  const pending = sessions
    .filter((s) => s.status === 'submitted' || s.status === 'revised')
    .sort((a, b) => {
      const ta = (a.submittedAt || a.updatedAt).getTime();
      const tb = (b.submittedAt || b.updatedAt).getTime();
      return ta - tb; // oldest first
    });

  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  const reviewed = sessions
    .filter((s) => s.status === 'approved' || s.status === 'needs-revision')
    .filter((s) => {
      const ref = (s.approvedAt || s.revisionRequestedAt || s.updatedAt).getTime();
      return ref >= sevenDaysAgo;
    })
    .sort((a, b) => {
      const ra = (a.approvedAt || a.revisionRequestedAt || a.updatedAt).getTime();
      const rb = (b.approvedAt || b.revisionRequestedAt || b.updatedAt).getTime();
      return rb - ra;
    });

  const openReview = (s: Session) => {
    setSelected(s);
    setDictationOpen(false);
    setError(null);
  };

  const closeReview = () => {
    setSelected(null);
    setShowApprove(false);
    setShowRevision(false);
    setSignature('');
    setComments('');
  };

  // Ask Claude to write the parent summary for an approved session.
  const runParentSummary = async (s: Session) => {
    setApprovedFlow((f) => (f ? { ...f, loading: true, error: false } : f));
    try {
      const start = s.actualStart || s.scheduledStart;
      const end = s.actualEnd || s.scheduledEnd;
      const mins = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
      const duration = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
      const summary = await parentSummaryService.generate(s.noteData || {}, s.noteTemplate, {
        clientName: s.clientName,
        sessionDate: start.toLocaleDateString(),
        sessionTime: start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        duration,
        sessionType: SESSION_TYPE_LABELS[s.sessionType],
        rbtName: s.rbtName,
      });
      setApprovedFlow((f) => (f ? { ...f, summary, loading: false, error: false, edited: false } : f));
    } catch (e) {
      console.error('parent summary generation failed:', e);
      // Fallback: let the BCBA write it manually (edited = true).
      setApprovedFlow((f) => (f ? { ...f, summary: f.summary || '', loading: false, error: true, edited: true } : f));
    }
  };

  const confirmApprove = async () => {
    if (!user || !selected) return;
    setSubmitting(true);
    try {
      // Approve + sign first (no summary yet); the summary is generated next.
      await sessionService.approveSession(user.companyId, selected.id, signature.trim());
      const s = selected;
      setShowApprove(false);
      setSelected(null);
      setApprovedFlow({ session: s, summary: '', loading: true, error: false, edited: false });
      await runParentSummary(s);
    } catch (e) {
      console.error('approve error:', e);
      setError('Could not approve. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const approveSummary = async () => {
    if (!user || !approvedFlow) return;
    setSubmitting(true);
    try {
      await sessionService.saveParentSummary(
        user.companyId,
        approvedFlow.session.id,
        approvedFlow.summary,
        approvedFlow.edited
      );
      setApprovedFlow(null);
    } catch (e) {
      console.error('save parent summary error:', e);
      setError('Could not save the parent summary. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Generate/download the branded clinical PDF and persist its Storage URL.
  // Re-fetches to capture the just-approved signature/timestamp; merges the
  // current (possibly unsaved) parent summary.
  const downloadPdf = async (session: Session, summaryOverride?: string) => {
    if (!user) return;
    setPdfBusy(true);
    try {
      const fresh = (await sessionService.getSession(user.companyId, session.id)) || session;
      const merged =
        summaryOverride != null ? { ...fresh, parentSummary: summaryOverride } : fresh;
      const url = await generateAndStoreSessionPdf(merged, company);
      await sessionService.saveNotePdfUrl(user.companyId, session.id, url);
    } catch (e) {
      console.error('PDF export error:', e);
      setError('Could not generate the PDF. Please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const confirmRevision = async () => {
    if (!user || !selected) return;
    setSubmitting(true);
    try {
      await sessionService.requestRevision(user.companyId, selected.id, comments.trim());
      setShowRevision(false);
      closeReview();
    } catch (e) {
      console.error('revision error:', e);
      setError('Could not send for revision. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------- parent summary confirmation --------------------- */
  if (approvedFlow) {
    const af = approvedFlow;
    const noteFields = fieldsFor(af.session.noteTemplate);
    const noteData = (af.session.noteData as Record<string, string> | null) || {};
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
          <CheckCircle2 size={24} style={{ color: 'var(--color-success)' }} />
          <h1 style={sectionTitle}>Approved &amp; Signed</h1>
        </div>

        {af.loading ? (
          <div style={{ ...card, textAlign: 'center', padding: 'var(--spacing-10)' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: '50%',
                backgroundColor: 'var(--color-light-accent)',
                color: 'var(--color-primary)',
                marginBottom: 'var(--spacing-4)',
                animation: 'vera-ai-pulse 1.4s ease-in-out infinite',
              }}
            >
              <FileText size={26} />
            </div>
            <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', marginBottom: '4px' }}>
              VERA is writing the parent summary…
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>For {af.session.clientName}.</div>
            <style>{`@keyframes vera-ai-pulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.08); opacity: 0.6; } }`}</style>
          </div>
        ) : (
          <div style={{ ...card, marginBottom: 'var(--spacing-5)' }}>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-4)' }}>
              The note for <strong>{af.session.clientName}</strong> is approved.{' '}
              {af.error
                ? "VERA couldn't write the summary — please write it manually. The clinical note is shown below for reference."
                : 'Review and edit the parent summary below, then approve it.'}
            </p>

            {af.error && (
              <div style={{ ...card, marginBottom: 'var(--spacing-4)', backgroundColor: 'var(--color-gray-50)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 'var(--spacing-2)' }}>
                  Clinical note (for reference)
                </div>
                {noteFields
                  .filter((f) => noteData[f.key])
                  .map((f) => (
                    <div key={f.key} style={{ marginBottom: 'var(--spacing-2)' }}>
                      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-secondary)' }}>{f.label}</div>
                      <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-primary)', whiteSpace: 'pre-wrap' }}>{noteData[f.key]}</div>
                    </div>
                  ))}
              </div>
            )}

            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-2)' }}>
              Parent Summary
            </label>
            <textarea
              rows={12}
              style={{ ...inputStyle, resize: 'vertical' }}
              value={af.summary}
              placeholder={af.error ? 'Write a warm, plain-language recap for the parent…' : ''}
              onChange={(e) => setApprovedFlow((f) => (f ? { ...f, summary: e.target.value, edited: true } : f))}
            />

            <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)', flexWrap: 'wrap' }}>
              <button
                style={{ ...btnPrimary, backgroundColor: 'var(--color-success)', opacity: af.summary.trim() && !submitting ? 1 : 0.6 }}
                onClick={approveSummary}
                disabled={!af.summary.trim() || submitting}
              >
                <CheckCircle2 size={16} />
                {submitting ? 'Saving…' : 'Approve Summary'}
              </button>
              <button
                style={{ ...btnGhost, opacity: submitting ? 0.6 : 1 }}
                onClick={() => runParentSummary(af.session)}
                disabled={submitting}
              >
                <RefreshCw size={16} />
                Regenerate
              </button>
              <button
                style={{ ...btnGhost, opacity: pdfBusy ? 0.6 : 1 }}
                onClick={() => downloadPdf(af.session, af.summary)}
                disabled={pdfBusy}
                title="Generate the branded clinical PDF"
              >
                <Download size={16} />
                {pdfBusy ? 'Generating…' : 'Download PDF'}
              </button>
              <button
                style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }}
                onClick={() => setApprovedFlow(null)}
                disabled={submitting}
              >
                Skip for now
              </button>
            </div>
            {af.edited && !af.error && (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-3)' }}>
                Edited from VERA's draft — saved as your version.
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ------------------------------ review view ---------------------------- */
  if (selected) {
    const template = getTemplate(selected.noteTemplate);
    const fields = fieldsFor(selected.noteTemplate);
    const data = (selected.noteData as Record<string, string> | null) || {};
    const gps = selected.gpsCheckIn;
    const gpsLabel = gps ? (gps.verified ? 'Verified' : 'Unverified') : 'No check-in';

    return (
      <div style={{ maxWidth: 760, margin: '0 auto', paddingBottom: 96 }}>
        <button
          onClick={closeReview}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            background: 'none',
            border: 'none',
            color: 'var(--color-primary)',
            cursor: 'pointer',
            marginBottom: 'var(--spacing-4)',
            fontWeight: 'var(--font-medium)',
          }}
        >
          <ArrowLeft size={18} />
          Back to list
        </button>

        {error && (
          <div style={{ ...card, marginBottom: 'var(--spacing-4)', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
            {error}
          </div>
        )}

        {/* Metadata */}
        <div style={{ ...card, marginBottom: 'var(--spacing-5)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ ...sectionTitle, fontSize: 'var(--text-2xl)', marginBottom: '4px' }}>{selected.clientName}</h1>
              <div style={meta}><UserIcon size={15} /> RBT: {selected.rbtName || '—'}</div>
            </div>
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--color-primary)',
                backgroundColor: 'var(--color-light-accent)',
                borderRadius: 'var(--radius-md)',
                padding: '4px 10px',
              }}
            >
              {SESSION_TYPE_LABELS[selected.sessionType]} · {template.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-5)', flexWrap: 'wrap', marginTop: 'var(--spacing-3)' }}>
            <div style={meta}><Clock size={15} /> {fmtDateTime(selected.actualStart || selected.scheduledStart)}</div>
            <div style={meta}><Clock size={15} /> Duration: {durationLabel(selected)}</div>
            <div style={meta}>
              <MapPin size={15} style={{ color: gps?.verified ? 'var(--color-success)' : 'var(--color-warning)' }} />
              GPS: {gpsLabel}
            </div>
          </div>
          {selected.verificationPhotoUrl && (
            <div style={{ marginTop: 'var(--spacing-4)' }}>
              <div style={{ ...meta, marginBottom: '6px' }}>Verification photo</div>
              <img
                src={selected.verificationPhotoUrl}
                alt="Session verification"
                style={{ maxWidth: 200, borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}
              />
            </div>
          )}
        </div>

        {/* Note body */}
        <div style={{ ...card, marginBottom: 'var(--spacing-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
            <FileText size={18} style={{ color: 'var(--color-primary)' }} />
            <h2 style={{ ...sectionTitle, fontSize: 'var(--text-lg)' }}>Clinical Note — {template.label}</h2>
          </div>
          <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
            {fields.map((f) => (
              <div key={f.key}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                  {f.label}
                </div>
                <div style={{ fontSize: 'var(--text-sm)', color: data[f.key] ? 'var(--color-text-primary)' : 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {data[f.key] || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Original dictation (collapsible) */}
        {selected.dictationTranscript && (
          <div style={{ ...card, marginBottom: 'var(--spacing-5)' }}>
            <button
              onClick={() => setDictationOpen((o) => !o)}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-primary)', fontWeight: 'var(--font-semibold)', width: '100%' }}
            >
              {dictationOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              Original Dictation
            </button>
            {dictationOpen && (
              <div style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {selected.dictationTranscript}
              </div>
            )}
          </div>
        )}

        {/* Audio player */}
        {selected.dictationAudioUrl && (
          <div style={{ ...card, marginBottom: 'var(--spacing-5)' }}>
            <div style={{ ...meta, marginBottom: 'var(--spacing-2)' }}>
              <Volume2 size={16} /> Raw dictation audio
            </div>
            <audio controls src={selected.dictationAudioUrl} style={{ width: '100%' }} />
          </div>
        )}

        {/* Sticky action bar */}
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--color-white)',
            borderTop: '1px solid var(--color-border)',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
            padding: 'var(--spacing-3) var(--spacing-6)',
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            zIndex: 90,
          }}
        >
          <button
            style={{ ...btnPrimary, backgroundColor: 'var(--color-success)' }}
            onClick={() => {
              setSignature(user ? `${user.name}${user.credentials ? `, ${user.credentials}` : ''}` : '');
              setShowApprove(true);
            }}
          >
            <CheckCircle2 size={16} />
            Approve &amp; Sign
          </button>
          <button
            style={{ ...btnPrimary, backgroundColor: 'var(--color-error)' }}
            onClick={() => {
              setComments('');
              setShowRevision(true);
            }}
          >
            <RotateCcw size={16} />
            Request Revision
          </button>
          <button style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }} onClick={closeReview}>
            Cancel
          </button>
        </div>

        {/* Approve modal */}
        {showApprove && (
          <div style={overlay} onClick={() => !submitting && setShowApprove(false)}>
            <div style={modalCard} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ ...sectionTitle, fontSize: 'var(--text-lg)', marginBottom: 'var(--spacing-3)' }}>Approve &amp; Sign</h2>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-3)' }}>
                Your electronic signature will be recorded with a timestamp.
              </p>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                Signature
              </label>
              <input style={inputStyle} value={signature} onChange={(e) => setSignature(e.target.value)} />
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
                <button style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }} onClick={() => setShowApprove(false)} disabled={submitting}>
                  Cancel
                </button>
                <button
                  style={{ ...btnPrimary, backgroundColor: 'var(--color-success)', opacity: signature.trim() && !submitting ? 1 : 0.6 }}
                  onClick={confirmApprove}
                  disabled={!signature.trim() || submitting}
                >
                  <CheckCircle2 size={16} />
                  {submitting ? 'Signing…' : 'Confirm Approval'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revision modal */}
        {showRevision && (
          <div style={overlay} onClick={() => !submitting && setShowRevision(false)}>
            <div style={modalCard} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ ...sectionTitle, fontSize: 'var(--text-lg)', marginBottom: 'var(--spacing-3)' }}>Request Revision</h2>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-3)' }}>
                The RBT will see these comments on their Needs Revision card.
              </p>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-2)' }}>
                Feedback for the RBT
              </label>
              <textarea
                rows={6}
                style={{ ...inputStyle, resize: 'vertical' }}
                value={comments}
                placeholder="Be specific about what needs to change…"
                onChange={(e) => setComments(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)', justifyContent: 'flex-end' }}>
                <button style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }} onClick={() => setShowRevision(false)} disabled={submitting}>
                  Cancel
                </button>
                <button
                  style={{ ...btnPrimary, backgroundColor: 'var(--color-error)', opacity: comments.trim() && !submitting ? 1 : 0.6 }}
                  onClick={confirmRevision}
                  disabled={!comments.trim() || submitting}
                >
                  <RotateCcw size={16} />
                  {submitting ? 'Sending…' : 'Send for Revision'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ------------------------------ list view ------------------------------ */
  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-5)' }}>
        <ClipboardCheck size={24} style={{ color: 'var(--color-primary)' }} />
        <h1 style={{ ...sectionTitle, fontSize: 'var(--text-2xl)' }}>Review Notes</h1>
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 'var(--spacing-5)', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
          {error}
        </div>
      )}

      {/* Pending review */}
      <section style={{ marginBottom: 'var(--spacing-10)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
          <h2 style={sectionTitle}>Pending Review</h2>
          {pending.length > 0 && (
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: 'var(--color-white)', backgroundColor: 'var(--color-primary)', borderRadius: '999px', padding: '2px 8px' }}>
              {pending.length}
            </span>
          )}
        </div>
        {pending.length === 0 ? (
          <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)', padding: 'var(--spacing-8)' }}>
            No notes are awaiting review.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--spacing-4)' }}>
            {pending.map((s) => {
              const isRevised = s.status === 'revised';
              return (
                <div
                  key={s.id}
                  onClick={() => openReview(s)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') openReview(s); }}
                  style={{
                    ...card,
                    cursor: 'pointer',
                    ...(isRevised
                      ? { borderLeft: '4px solid #3B82F6', backgroundColor: 'rgba(59,130,246,0.05)' }
                      : {}),
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>
                          {s.clientName}
                        </span>
                        {isRevised && (
                          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-semibold)', color: '#1E40AF', backgroundColor: 'rgba(59,130,246,0.15)', borderRadius: '999px', padding: '2px 10px' }}>
                            Revised &amp; Resubmitted
                          </span>
                        )}
                      </div>
                      <div style={{ ...meta, marginBottom: '2px' }}>
                        <UserIcon size={14} /> {s.rbtName || 'RBT'} · {SESSION_TYPE_LABELS[s.sessionType]} · {getTemplate(s.noteTemplate).label}
                      </div>
                      <div style={meta}>
                        <Clock size={14} /> {fmtDateTime(s.actualStart || s.scheduledStart)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>Submitted</div>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-primary)' }}>
                        {timeSince(s.submittedAt, now)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Reviewed (collapsible) */}
      <section>
        <button
          onClick={() => setReviewedOpen((o) => !o)}
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 'var(--spacing-4)' }}
        >
          {reviewedOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <span style={sectionTitle}>Reviewed</span>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            (last 7 days · {reviewed.length})
          </span>
        </button>
        {reviewedOpen &&
          (reviewed.length === 0 ? (
            <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)', padding: 'var(--spacing-6)' }}>
              No sessions reviewed in the last 7 days.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
              {reviewed.map((s) => {
                const approved = s.status === 'approved';
                return (
                  <div key={s.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-4)', flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => openReview(s)}>
                    <div>
                      <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>{s.clientName}</div>
                      <div style={meta}>
                        <UserIcon size={14} /> {s.rbtName || 'RBT'} · {fmtDateTime(s.actualStart || s.scheduledStart)}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        borderRadius: '999px',
                        padding: '3px 10px',
                        color: approved ? '#166534' : '#991B1B',
                        backgroundColor: approved ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.12)',
                      }}
                    >
                      {approved ? 'Approved' : 'Sent for Revision'}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
      </section>
    </div>
  );
};

export default BcbaReviewNotes;
